import { Children, Component, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Bell, BellOff, X } from 'lucide-react';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import {
  askPermission,
  loadPrefs,
  permission,
  savePrefs,
  shouldOfferPrompt,
  supported,
} from '../../lib/browserNotify';
import { anyHeld } from '../../lib/snoozeHold';

/**
 * NotificationPanelShell — the bell dropdown, rendered where it can be seen.
 * (notify-center, 7 Sep 2026)
 *
 * WHY THIS EXISTS (the off-screen bug)
 * ------------------------------------
 * The panel used to be a plain `absolute right-0 mt-2 ... z-50` div nested
 * inside `<header className="header">`.  App.css gives that header
 * `sticky top-0 z-30 ... backdrop-blur-sm`, and an element with a non-none
 * `backdrop-filter` becomes the CONTAINING BLOCK for every positioned
 * descendant (fixed ones included) and opens its own stacking context.  So the
 * panel was never laid out against the viewport at all — it was laid out and
 * painted against a ~72px bar pinned at the very top of the page, behind the
 * phone's address bar, underneath the z-40 sidebar, with a hard-coded
 * `max-h-96` that knew nothing about how much screen was actually left.
 *
 * The fix is to stop being a descendant.  This shell portals to
 * `document.body`, measures the bell with `getBoundingClientRect()`, and
 * positions itself with `position: fixed` against `window.visualViewport`
 * (the viewport that actually shrinks when the mobile address bar is showing),
 * clamped by `env(safe-area-inset-*)` and capped at `min(70vh, room left)`.
 * It cannot render above the top of the screen, and it can never be taller
 * than the space it has.
 */

const GUTTER = 8;
const GAP = 8;
const DESKTOP_WIDTH = 384; // w-96
const MOBILE_BREAKPOINT = 640;
const Z = 2147483000; // above the sidebar (z-40) and the sonner toaster

const cssPx = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

/** Read env(safe-area-inset-*) without hard-coding a phone model. */
function safeAreaInsets() {
  if (typeof document === 'undefined' || !document.body) return { top: 0, bottom: 0 };
  const probe = document.createElement('div');
  probe.style.cssText =
    'position:fixed;top:0;left:-9999px;width:0;height:0;' +
    'padding-top:env(safe-area-inset-top,0px);padding-bottom:env(safe-area-inset-bottom,0px);';
  document.body.appendChild(probe);
  let top = 0;
  let bottom = 0;
  try {
    const cs = window.getComputedStyle(probe);
    top = cssPx(parseFloat(cs.paddingTop));
    bottom = cssPx(parseFloat(cs.paddingBottom));
  } catch (e) { /* jsdom */ } finally {
    probe.remove();
  }
  return { top, bottom };
}

/**
 * Where the panel goes, given the bell's rectangle.
 * Exported so it can be unit-tested without a browser.
 */
export function computePosition(anchorRect, viewport, insets) {
  const vw = viewport.width;
  const vh = viewport.height;
  const safeTop = (viewport.offsetTop || 0) + insets.top + GUTTER;
  const safeBottom = (viewport.offsetTop || 0) + vh - insets.bottom - GUTTER;
  const mobile = vw < MOBILE_BREAKPOINT;

  const width = mobile
    ? Math.max(240, vw - GUTTER * 2)
    : Math.min(DESKTOP_WIDTH, vw - GUTTER * 2);

  // Anchored under the bell...
  let top = (anchorRect ? anchorRect.bottom : safeTop) + GAP;
  // ...but never above the safe area, and never past the bottom edge.
  top = Math.max(safeTop, top);
  top = Math.min(top, Math.max(safeTop, safeBottom - 120));

  let left;
  if (mobile) {
    left = GUTTER;
  } else {
    const right = anchorRect ? anchorRect.right : vw - GUTTER;
    left = right - width;
    left = Math.max(GUTTER, Math.min(left, vw - width - GUTTER));
  }

  const room = Math.max(160, safeBottom - top);
  const maxHeight = Math.min(Math.round(vh * 0.7), room);

  return { top: Math.round(top), left: Math.round(left), width: Math.round(width), maxHeight };
}

function readViewport() {
  if (typeof window === 'undefined') return { width: 1024, height: 768, offsetTop: 0 };
  const vv = window.visualViewport;
  if (vv) return { width: vv.width, height: vv.height, offsetTop: vv.offsetTop || 0 };
  return {
    width: window.innerWidth || 1024,
    height: window.innerHeight || 768,
    offsetTop: 0,
  };
}

/**
 * PanelBoundary — the bell lives in the Header, so anything that throws while
 * the list renders takes down EVERY page.  The legacy `notifications`
 * collection is not uniform (rows carry object fields such as
 * `from: {name, email}`, and at least one mail-chime `{new, mailbox}` payload),
 * and React throws "Objects are not valid as a React child" on those.
 * lib/renderSafe.js stops the known shapes; this stops the unknown ones.
 * Worst case the panel shows one line of apology and the app stays up.
 */
class PanelBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error, info) {
    // Kept in the console so a bad row can still be traced on the box.
    try { console.error('Notification panel: a row failed to render', error, info); }
    catch (e) { /* no console */ }
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="px-4 py-6 text-center text-xs text-slate-500 flex flex-col items-center gap-2"
           data-testid="notification-panel-error">
        <AlertTriangle className="w-4 h-4 text-amber-500" />
        <span>One of these notifications could not be displayed.</span>
        <button
          type="button"
          className="text-blue-700 hover:underline"
          onClick={() => this.setState({ failed: false })}
        >
          Try again
        </button>
      </div>
    );
  }
}

/** The dismissible one-line permission strip. */
function DesktopAlertStrip({ userId }) {
  const [prefs, setPrefs] = useState(() => loadPrefs(userId));
  const [state, setState] = useState(() => permission());

  const persist = (next) => {
    setPrefs(next);
    savePrefs(userId, next);
    api.put('/notify-center/prefs', next).catch(() => {});
  };

  if (!supported()) {
    return (
      <div className="px-4 py-2 text-xs text-slate-500 border-b border-slate-100 bg-slate-50">
        This browser cannot show desktop alerts. The bell here still works.
      </div>
    );
  }

  if (state === 'denied') {
    return (
      <div className="px-4 py-2 text-xs text-slate-500 border-b border-slate-100 bg-slate-50 flex items-start gap-2"
           data-testid="notify-denied-line">
        <BellOff className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-slate-400" />
        <span>
          Desktop alerts are blocked for this site. Click the lock icon in the address bar →
          Notifications → Allow, then reload. The bell keeps working either way.
        </span>
      </div>
    );
  }

  if (state !== 'default' || !shouldOfferPrompt(userId, prefs)) return null;

  return (
    <div className="px-4 py-2 border-b border-slate-100 bg-amber-50/70 flex items-center gap-2"
         data-testid="notify-permission-strip">
      <Bell className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
      <button
        type="button"
        className="text-xs font-medium text-amber-900 hover:underline flex-1 text-left"
        data-testid="notify-permission-ask"
        onClick={async () => {
          // A real click — this is the only place requestPermission() is called.
          const result = await askPermission();
          setState(result);
          if (result !== 'default') persist({ ...prefs, prompt_dismissed: true });
        }}
      >
        Turn on desktop alerts
      </button>
      <button
        type="button"
        aria-label="Not now"
        className="text-slate-400 hover:text-slate-600"
        data-testid="notify-permission-dismiss"
        onClick={() => persist({ ...prefs, prompt_dismissed: true })}
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

/**
 * Wrap whatever the Header already renders inside the dropdown.
 * `anchorRef` is the element the panel hangs under (the bell, or its wrapper).
 */
export default function NotificationPanelShell({ anchorRef, onClose, children }) {
  const auth = useAuth() || {};
  const userId = auth.user?.id;
  const boxRef = useRef(null);
  const [pos, setPos] = useState(null);

  const measure = useCallback(() => {
    const el = anchorRef && anchorRef.current;
    const rect = el && el.getBoundingClientRect ? el.getBoundingClientRect() : null;
    setPos(computePosition(rect, readViewport(), safeAreaInsets()));
  }, [anchorRef]);

  useLayoutEffect(() => {
    measure();
  }, [measure]);

  useEffect(() => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    window.addEventListener('orientationchange', measure);
    if (vv) {
      vv.addEventListener('resize', measure);
      vv.addEventListener('scroll', measure);
    }
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('orientationchange', measure);
      if (vv) {
        vv.removeEventListener('resize', measure);
        vv.removeEventListener('scroll', measure);
      }
    };
  }, [measure]);

  // Escape closes; the Header's own click-outside handler still runs, and it
  // checks its own ref — which no longer contains us — so we guard clicks here.
  //
  // While a snooze menu is open the panel refuses to close on Escape: the menu
  // takes that Escape for itself (it stops the keydown), and a stray one must
  // not pull the panel out from under a half-made decision. A deliberate click
  // outside still closes, as it always did.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !anyHeld() && onClose) onClose(); };
    const onDown = (e) => {
      const inPanel = boxRef.current && boxRef.current.contains(e.target);
      const inAnchor = anchorRef && anchorRef.current && anchorRef.current.contains(e.target);
      if (!inPanel && !inAnchor && onClose) onClose();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [anchorRef, onClose]);

  if (typeof document === 'undefined' || !document.body) return null;
  const p = pos || computePosition(null, readViewport(), { top: 0, bottom: 0 });
  // The strip sits under the panel's own header row, not above it.
  const kids = Children.toArray(children);

  return createPortal(
    <div
      ref={boxRef}
      role="dialog"
      aria-label="Notifications"
      data-testid="notification-panel"
      className="rounded-xl bg-white shadow-2xl border border-slate-200 overflow-hidden flex flex-col animate-in"
      style={{
        position: 'fixed',
        top: p.top,
        left: p.left,
        width: p.width,
        maxHeight: p.maxHeight,
        zIndex: Z,
      }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <PanelBoundary>{kids[0]}</PanelBoundary>
      <DesktopAlertStrip userId={userId} />
      <PanelBoundary>{kids.slice(1)}</PanelBoundary>
    </div>,
    document.body,
  );
}
